import { BrandLogo, Eyebrow } from '@fruuit/ui'
import { FileText, Handshake, ShieldCheck } from 'lucide-react'
import type { ReactNode } from 'react'

const POINTS = [
  {
    icon: ShieldCheck,
    title: 'Independently reviewed findings',
    body: 'Every finding is approved by a second reviewer before it reaches you.',
  },
  {
    icon: Handshake,
    title: 'Engagement you can follow',
    body: 'Objectives, milestones, correspondence and meetings on one record.',
  },
  {
    icon: FileText,
    title: 'Committee-ready reporting',
    body: 'Preview and export with exactly the same scope.',
  },
]

/** Brand panel + form column for sign-in, code entry and invitations. */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh bg-cream">
      <aside className="relative hidden w-[44%] max-w-[40rem] shrink-0 flex-col overflow-hidden border-r border-line bg-wash px-12 py-12 lg:flex xl:px-16">
        <BrandLogo className="w-44" />
        <div className="my-auto max-w-md py-12">
          <Eyebrow>Fruuit Engage</Eyebrow>
          <p className="mt-3 font-display text-[34px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink">
            Research-led stewardship, in one connected view.
          </p>
          <p className="mt-4 text-base leading-relaxed text-sage">
            Follow your portfolio companies, the engagement record and the reviewed evidence behind
            every conclusion.
          </p>
          <ul className="mt-10 flex flex-col gap-6">
            {POINTS.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-4">
                <span
                  aria-hidden="true"
                  className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface text-teal shadow-card ring-1 ring-line"
                >
                  <Icon className="size-5" />
                </span>
                <div>
                  <p className="text-[15px] font-semibold text-ink">{title}</p>
                  <p className="mt-0.5 text-sm leading-relaxed text-sage">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <p className="flex items-center gap-2 text-xs text-sage">
          <span aria-hidden="true" className="size-1.5 rounded-full bg-leaf" />
          Demo platform. All companies, people and data are fictional.
        </p>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <main className="flex flex-1 flex-col items-center justify-center px-5 py-10 sm:px-8">
          <div className="w-full max-w-[25rem]">
            <BrandLogo className="mx-auto mb-8 w-36 lg:hidden" />
            {children}
          </div>
        </main>
        <footer className="px-6 pb-6 text-center text-xs text-sage lg:hidden">
          Demo platform. All companies, people and data are fictional.
        </footer>
      </div>
    </div>
  )
}

export function AuthHeading({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="mb-8">
      <h1 className="font-display text-[28px] font-semibold leading-tight tracking-[-0.02em] text-ink">
        {title}
      </h1>
      {children ? (
        <div className="mt-2.5 text-[15px] leading-relaxed text-sage">{children}</div>
      ) : null}
    </div>
  )
}
